import { useEffect, useState } from "react";
import axios from "axios";
import ContactForm from "../Components/ContactUs/ContactForm";
import { isMobile } from "react-device-detect";
import ToggleSwitch from "../Components/ContactUs/ToggleSwitch";
import ContactStyle from "./Contact.module.css";
import Thankyou from "../Components/Thankyou";

const INITIAL_DETAILS = {
  firstName: "",
  lastName: "",
  email: "",
  dob: "",
  bookingId: "",
  reason: "",
  zipCode: "",
  message: "",
};

const EMAIL_REGEX =
  /^([a-zA-Z0-9.-]+)@([a-zA-Z0-9-]+).([a-z]{2,8})(.[a-z]{1,8})$/;
const BOOKING_ID_REGEX = /^[A-Za-z0-9]{9}$/;
const BOOKING_ID_REQUIRED_REASONS = ["Complaint", "Refund", "Payout"];

const INITIAL_ERR_MSG_VALID = {
  isFirstName: false,
  isLastName: false,
  isEmail: false,
  isDob: false,
  isBookingId: false,
  isZipCode: false,
};

const Contactus = () => {
  const [isExperiencer, setIsExperiencer] = useState(true);
  const [localtype, setLocaltype] = useState("local");
  const [type, setType] = useState("date");
  const [showThankyou, setShowThankyou] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const [selectOpen, setSelectOpen] = useState(false);
  const [disablebtn, setDisablebtn] = useState(false);
  const [details, setDetails] = useState(INITIAL_DETAILS);

  const [errMsgValid, setErrMsgValid] = useState(INITIAL_ERR_MSG_VALID);

  const [disabled, setDisabled] = useState(true);

  const hasFirstName = details.firstName.trim() !== "";
  const hasLastName = details.lastName.trim() !== "";
  const hasEmail = details.email.trim() !== "";
  const hasReason = details.reason.trim() !== "";
  const hasMessage = details.message.trim() !== "";
  const hasBookingId = details.bookingId.trim() !== "";

  const validEmail = EMAIL_REGEX.test(details.email);
  const validBookingId = BOOKING_ID_REGEX.test(details.bookingId);

  // A booking id only exists for reasons tied to a booking. For the appeal and
  // "Other" reasons the user may not have one, so it stays optional there.
  const bookingIdRequired = BOOKING_ID_REQUIRED_REASONS.includes(details.reason);

  useEffect(() => {
    // Both flags in one update: the previous code called setErrMsgValid twice,
    // each spreading the same stale object, so the first change was discarded.
    setErrMsgValid((prev) => ({
      ...prev,
      isEmail: hasEmail && !validEmail,
      isBookingId: hasBookingId && !validBookingId,
    }));
  }, [hasEmail, validEmail, hasBookingId, validBookingId]);

  useEffect(() => {
    // dob and zipCode are deliberately not required: those inputs are commented
    // out of ContactForm, so requiring them kept the button disabled forever.
    const bookingIdOk = bookingIdRequired
      ? hasBookingId && validBookingId
      : !hasBookingId || validBookingId;

    const complete =
      hasFirstName &&
      hasLastName &&
      hasEmail &&
      validEmail &&
      hasReason &&
      hasMessage &&
      bookingIdOk;

    setDisabled(!complete);
  }, [
    hasFirstName,
    hasLastName,
    hasEmail,
    validEmail,
    hasReason,
    hasMessage,
    hasBookingId,
    validBookingId,
    bookingIdRequired,
  ]);
  useEffect(() => {
    if (isExperiencer) {
      setLocaltype("experiencer");
      setDetails({ ...details, reason: "" });
    } else {
      setLocaltype("local");
      setDetails({ ...details, reason: "" });
    }
  }, [isExperiencer]);

  useEffect(() => {
    console.log("Details state updated:", details);
  }, [details]);

  const handleInputChange = (e) => {
    const name = e.target.name;
    let value = e.target.value;
    if (name === "dob") {
      let val = value.split("-");
      let val1 = [val[1], val[2], val[0]];
      let val2 = val1.join("-");
      value = val2;
      if (!isMobile) {
        setTimeout(() => {
          setType("text");
          setDisablebtn(true);
        }, 2000);
      }
    }
    setDetails((values) => ({ ...values, [name]: value }));
    // if (name === "zipCode") {
    //   const regex = /^\d{5}$/;
    //   const validCode = regex.test(value);
    //   if (!validCode) {
    //     setErrMsgValid({ ...errMsgValid, isZipCode: true });
    //   } else {
    //     setErrMsgValid({ ...errMsgValid, isZipCode: false });
    //   }
    // }
    if (name === "email") {
      let validregex =
        /^([a-zA-Z0-9.-]+)@([a-zA-Z0-9-]+).([a-z]{2,8})(.[a-z]{1,8})$/;

      const validemail = validregex.test(value);
      if (!validemail) {
        setErrMsgValid({ ...errMsgValid, isEmail: true });
      } else {
        setErrMsgValid({ ...errMsgValid, isEmail: false });
      }
    }
  };

  // The inputs are controlled, so clearing state clears the fields. Doing it via
  // form.reset() instead would change the DOM behind React's back and leave its
  // value tracker stale, which silently swallows the next change event when a
  // field is re-entered with the same text.
  const resetForm = () => {
    setDetails(INITIAL_DETAILS);
    setErrMsgValid(INITIAL_ERR_MSG_VALID);
    setSelectOpen(false);
    setType("date");
    setDisablebtn(false);
  };

  const handleSubmit = (e) => {
    try {
      e.preventDefault();
      setSubmitError("");
      const newValues = { ...details, usertype: localtype };
      saveDetails(newValues);
    } catch (err) {
      console.log(err);
    }
  };
  const saveDetails = (payload) => {
    try {
      axios
        .post(`${process.env.REACT_APP_BASE_URL}/contact`, payload, {
          headers: {
            Authorization:
              "Basic " +
              btoa(
                process.env.REACT_APP_USERNAME +
                  ":" +
                  process.env.REACT_APP_PASSWORD
              ),
          },
        })
        .then((res) => {
          const body = res.data || {};
          // The API replies { status: true, statusCode: 200, message: "Success" }.
          // The old check was `body.status === 200`, which never matched because
          // `status` is a boolean and the numeric code lives in `statusCode` — so
          // the submission saved but no confirmation ever ran. Accept either shape.
          const code = body.statusCode ?? body.status;
          if (body.status === true || code === 200) {
            // Confirm in a modal rather than navigating to /thankyou: that route
            // is not registered in appRouter, so it rendered a 404 error page.
            resetForm();
            setSubmitError("");
            setShowThankyou(true);
          } else {
            setSubmitError(
              body?.error?.errorCode ||
                body.message ||
                "We could not send your message. Please try again."
            );
          }
        })
        .catch((err) => {
          // Previously only logged, so a failed submission looked like nothing
          // happened at all.
          console.log(err);
          setSubmitError(
            err?.response?.data?.error?.errorCode ||
              err?.response?.data?.message ||
              "We could not reach the server. Please check your connection and try again."
          );
        });
    } catch (err) {
      console.log(err);
    }
  };
  return (
    <>
      <section className={ContactStyle.contactUs}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-12 col-xl-11">
              <div>
                <h1 className={ContactStyle.title}>Contact Us</h1>
              </div>
              <ToggleSwitch
                isExperiencer={true}
                setIsExperiencer={(value) => setIsExperiencer(value)}
              />
              <ContactForm
                details={details}
                disabled={disabled}
                handleSubmit={handleSubmit}
                handleInputChange={handleInputChange}
                setDetails={setDetails}
                disablebtn={disablebtn}
                selectOpen={selectOpen}
                isExperiencer={isExperiencer}
                setSelectOpen={setSelectOpen}
                errMsgValid={errMsgValid}
                setType={setType}
                type={type}
                setDisablebtn={setDisablebtn}
                submitError={submitError}
              />
            </div>
          </div>
        </div>
      </section>
      <Thankyou
        show={showThankyou}
        modalHandler={setShowThankyou}
        title="Thanks for reaching out! We've received your submission."
        subtitle="Our team will review it and get back to you by email."
        showAppLinks={false}
        showSocial={false}
        autoCloseMs={5000}
      />
    </>
  );
};

export default Contactus;
